import { get } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";

// Curated (curationMode = PARQUET) datasets are read over a time range through GET /api/datasets/{id}/query on
// erodeapi, instead of one stored file at a time. See docs/plans/DATASET_API_INGEST_PLAN.md ("Stage 4 design").

export interface DatasetQueryColumn {
  name: string;
  type: string;
  /** What a chart calls the column (from the dataset's mapping); the name when null */
  label?: string | null;
  unit: string | null;
  description: string | null;
  aggregation: string | null;
  /** observation | derived | forecast when the processor declared it (a processed output's query); absent otherwise. */
  kind?: string | null;
}

export interface DatasetQueryAggregation {
  applied: boolean;
  interval: string | null;
  method: string | null;
  rawRows: number;
}

export interface DatasetQueryResult {
  datasetId: string;
  range: { start: string | null; end: string | null };
  columns: DatasetQueryColumn[];
  timestamps: string[];
  series: Record<string, Array<number | string | boolean | null>>;
  aggregation: DatasetQueryAggregation;
  files: number;
  warnings: string[];
}

export interface DatasetQueryParams {
  start?: string | null;
  end?: string | null;
  columns?: string[];
  maxPoints?: number;
  agg?: string;
  interval?: string;
}

export interface DatasetChartRow {
  id: number;
  timestamp: string;
  [column: string]: unknown;
}

// What one chart can usefully draw; the endpoint aggregates past it
export const DATASET_CHART_MAX_POINTS = 2000;

const INTERVAL_LABELS: Record<string, string> = {
  "1m": "1-minute",
  "5m": "5-minute",
  "10m": "10-minute",
  "15m": "15-minute",
  "30m": "30-minute",
  "1h": "hourly",
  "2h": "2-hour",
  "3h": "3-hour",
  "6h": "6-hour",
  "12h": "12-hour",
  "1d": "daily",
  "7d": "weekly",
};

const METHOD_LABELS: Record<string, string> = {
  avg: "averages",
  min: "minimums",
  max: "maximums",
  sum: "totals",
  first: "first values",
  last: "last values",
};

/** A historical dataset whose uploads are merged into curated Parquet, so it is read by time range */
export const isCuratedDataset = (dataset: any): boolean =>
  dataset?.curationMode === "PARQUET" && dataset?.datasetType === "HISTORICAL";

/**
 * The picker's dates → the endpoint's range. The picker's end is a day the user expects to be included, and the
 * endpoint's end is exclusive, so the end moves to the start of the next day. In UTC: the picker's dates are UTC
 * midnights and the chart's axis is UTC, so a local end-of-day would cut most of the last day off for anyone west
 * of Greenwich (17 hours of it in Seattle).
 */
export const buildDatasetQueryParams = (
  range: { start?: Date | string | null; end?: Date | string | null } | null | undefined
): DatasetQueryParams => {
  const params: DatasetQueryParams = { maxPoints: DATASET_CHART_MAX_POINTS };
  if (range?.start) params.start = new Date(range.start).toISOString();
  if (range?.end) {
    const end = new Date(range.end);
    end.setUTCHours(23, 59, 59, 999);
    params.end = new Date(end.getTime() + 1).toISOString();
  }
  return params;
};

/** Keep a range inside the dataset's own first and last timestamps; any length, since the endpoint downsamples */
export const clampRangeToBounds = (
  start: Date | string | null | undefined,
  end: Date | string | null | undefined,
  bounds: { minDate?: Date | string | null; maxDate?: Date | string | null } = {}
): { start: Date | null; end: Date | null } => {
  const min = bounds.minDate ? new Date(bounds.minDate) : null;
  const max = bounds.maxDate ? new Date(bounds.maxDate) : null;
  let startDate = start ? new Date(start) : min;
  let endDate = end ? new Date(end) : max;
  if (min && startDate && startDate < min) startDate = new Date(min);
  if (max && endDate && endDate > max) endDate = new Date(max);
  if (startDate && endDate && startDate > endDate) startDate = new Date(endDate);
  return { start: startDate ?? null, end: endDate ?? null };
};

/** The endpoint's columnar answer → the row objects the dataset chart and the CSV download already take */
export type SeriesQueryAnswer = Pick<DatasetQueryResult, "columns" | "timestamps" | "series" | "aggregation" | "warnings">;

export const queryResultToRows = (result: SeriesQueryAnswer | null | undefined): DatasetChartRow[] => {
  if (!result?.timestamps?.length) return [];
  const names = result.columns.map((column) => column.name);
  return result.timestamps.map((timestamp, index) => {
    const row: DatasetChartRow = { id: index, timestamp };
    names.forEach((name) => {
      row[name] = result.series[name]?.[index] ?? null;
    });
    return row;
  });
};

/**
 * The CSV's columns, from the answer. Not from Dataset.columns: every upload overwrites that with its own columns
 * (#851), and a download must carry every series the chart is showing.
 */
export const queryResultColumnOrder = (result: SeriesQueryAnswer | null | undefined): string[] =>
  result ? ["timestamp", ...result.columns.map((column) => column.name)] : [];

/** Units and descriptions as the chart takes them, from the answer itself */
export const queryResultColumnInfo = (
  result: SeriesQueryAnswer | null | undefined
): Record<string, { label?: string; unit?: string; description?: string }> => {
  const info: Record<string, { label?: string; unit?: string; description?: string }> = {};
  (result?.columns || []).forEach((column) => {
    if (column.label || column.unit || column.description) {
      info[column.name] = {
        ...(column.label ? { label: column.label } : {}),
        ...(column.unit ? { unit: column.unit } : {}),
        ...(column.description ? { description: column.description } : {}),
      };
    }
  });
  return info;
};

/** "Showing hourly averages of 17,544 rows", or null when the chart holds the rows themselves */
export const describeQueryAggregation = (result: SeriesQueryAnswer | null | undefined): string | null => {
  const aggregation = result?.aggregation;
  if (!aggregation?.applied || !aggregation.interval) return null;
  // Past the ladder the endpoint uses whole weeks: "14d", "21d", …
  const days = /^(\d+)d$/.exec(aggregation.interval);
  const interval = INTERVAL_LABELS[aggregation.interval] || (days ? `${days[1]}-day` : aggregation.interval);
  const method = METHOD_LABELS[aggregation.method || "avg"] || aggregation.method;
  // Text and true/false columns cannot be averaged; the endpoint gives each bucket's last value for those
  const mixed = (result?.columns || []).some((column) => column.aggregation && column.aggregation !== aggregation.method);
  const note = `Showing ${interval} ${method} of ${aggregation.rawRows.toLocaleString()} rows`;
  return mixed ? `${note} (last value for text columns)` : note;
};

/** A failed query, with what the endpoint's { statusCode, error, code?, ... } envelope said */
export class DatasetQueryError extends Error {
  /** Machine-readable reason on a 400 (`cell_budget`, `response_too_large`), when the endpoint gave one */
  code: string | null;
  /** The rest of the envelope: `maxPointsAllowed` on a `cell_budget` answer, `maxBytes` on `response_too_large` */
  details: Record<string, unknown>;

  constructor(message: string, code: string | null = null, details: Record<string, unknown> = {}) {
    super(message);
    this.name = "DatasetQueryError";
    this.code = code;
    this.details = details;
  }
}

/** The failed request's envelope as a DatasetQueryError; a body that is not this API's JSON keeps the message */
export const toDatasetQueryError = (error: any): DatasetQueryError => {
  if (error instanceof DatasetQueryError) return error;
  const body = error?.response?.body;
  if (typeof body === "string") {
    try {
      const { statusCode, error: message, errors, code, ...details } = JSON.parse(body) || {};
      if (message) return new DatasetQueryError(message, typeof code === "string" ? code : null, details);
    } catch {
      // not JSON: fall through to the generic message
    }
  }
  return new DatasetQueryError(error?.message || "Failed to load this dataset's data.");
};

/**
 * A wide dataset asked for without naming columns can exceed the endpoint's budget of values per answer
 * (columns × maxPoints). Its 400 carries `code: "cell_budget"` and the maxPoints that fits; a chart would
 * rather draw fewer points than fail.
 */
export const maxPointsThatFits = (error: unknown): number | null => {
  if (!(error instanceof DatasetQueryError) || error.code !== "cell_budget") return null;
  const allowed = Number(error.details.maxPointsAllowed);
  return Number.isInteger(allowed) && allowed > 0 ? allowed : null;
};

export const fetchDatasetQuery = async (datasetId: string, params: DatasetQueryParams = {}): Promise<DatasetQueryResult> => {
  try {
    return await requestDatasetQuery(datasetId, params);
  } catch (error: any) {
    const fits = maxPointsThatFits(error);
    if (!fits || fits >= (params.maxPoints ?? Infinity)) throw error;
    return requestDatasetQuery(datasetId, { ...params, maxPoints: fits });
  }
};

const requestDatasetQuery = async (datasetId: string, params: DatasetQueryParams): Promise<DatasetQueryResult> => {
  const session = await fetchAuthSession();
  const idToken = session?.tokens?.idToken?.toString();
  if (!idToken) throw new Error("You need to be signed in to load this dataset.");

  const queryParams: Record<string, string> = {};
  if (params.start) queryParams.start = params.start;
  if (params.end) queryParams.end = params.end;
  if (params.columns?.length) queryParams.columns = JSON.stringify(params.columns);
  if (params.maxPoints) queryParams.maxPoints = String(params.maxPoints);
  if (params.agg) queryParams.agg = params.agg;
  if (params.interval) queryParams.interval = params.interval;

  try {
    // erodeapi authorizes with the Cognito token itself (tokenCheck), not with IAM signing
    const operation = get({
      apiName: "erodeapi",
      path: `/api/datasets/${encodeURIComponent(datasetId)}/query`,
      // Amplify retries 5xx three times by default: a query that timed out would run three times over before the
      // user heard about it. The modal has its own "Try again".
      options: { headers: { Authorization: `Bearer ${idToken}` }, queryParams, retryStrategy: { strategy: "no-retry" } },
    });
    const response = await operation.response;
    const payload = (await response.body.json()) as unknown as { body?: DatasetQueryResult };
    if (!payload?.body?.timestamps) throw new Error("Unexpected response from the dataset query.");
    return payload.body;
  } catch (error) {
    throw toDatasetQueryError(error);
  }
};

// ── Quality flags (qc_<column>) ───────────────────────────────────────────────────────────────────
// A historical model's processor (the quality rules) writes a `qc_<column>` flag column beside each column it cleans,
// stored in the dataset as an integer; a downsampled query answers a bucket's flags OR-ed together.

/** The flag column of a series column: `qc_<name>`. */
export const flagColumnFor = (column: string): string => `qc_${column}`;

/** A column name is a flag column when it carries the qc_ prefix; those are never offered as a line. */
export const isFlagColumn = (column: string): boolean => /^qc_/i.test(column);

/** The flag codes a historical processor sets, lowest to highest; they add up (5 = sentinel then filled). */
export const FLAG_CODES: { code: number; label: string; tooltip: string; color: string }[] = [
  { code: 1, label: "Sentinel removed", tooltip: "sentinel removed", color: "rgb(239, 68, 68)" },
  { code: 2, label: "Below minimum", tooltip: "below minimum, set", color: "rgb(245, 158, 11)" },
  { code: 4, label: "Gap filled", tooltip: "gap filled", color: "rgb(139, 92, 246)" },
  { code: 8, label: "Clamped", tooltip: "clamped", color: "rgb(236, 72, 153)" },
];

/** The codes present in a flag value, highest first: 5 → [4, 1]. */
export const flagCodesOf = (flag: number | null | undefined): number[] =>
  typeof flag === "number" && flag > 0 ? FLAG_CODES.map((f) => f.code).filter((code) => (flag & code) === code).reverse() : [];

/** "below minimum, set to 0" style words for a flag value; "" when nothing was changed. */
export const describeFlag = (flag: number | null | undefined, value?: number | null): string => {
  const codes = flagCodesOf(flag);
  if (!codes.length) return "";
  return codes
    .map((code) => {
      const entry = FLAG_CODES.find((f) => f.code === code);
      if (!entry) return "";
      if (code === 2 && value !== null && value !== undefined) return `below minimum, set to ${value}`;
      if (code === 8 && value !== null && value !== undefined) return `clamped to ${value}`;
      return entry.tooltip;
    })
    .filter(Boolean)
    .join(", ");
};

/** The colour of the most important code in a flag value (the highest one set). */
export const flagColor = (flag: number | null | undefined): string | null => {
  const [highest] = flagCodesOf(flag);
  return FLAG_CODES.find((f) => f.code === highest)?.color ?? null;
};

/** The value columns of a query answer, without their flag columns. */
export const valueColumnsOf = (result: Pick<DatasetQueryResult, "columns"> | null | undefined): string[] =>
  (result?.columns ?? []).map((c) => c.name).filter((name) => !isFlagColumn(name));
